import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CpuChipIcon,
  CircleStackIcon,
  ClockIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

interface SystemMetrics {
  tokenUsage: {
    today: number;
    thisWeek: number;
    thisMonth: number;
    total: number;
    averagePerConversation: number;
    costToday: number;
    costThisMonth: number;
  };
  apiPerformance: {
    averageResponseTime: number;
    totalRequests: number;
    failedRequests: number;
    successRate: number;
  };
  userActivity: {
    dailyActiveUsers: number;
    weeklyActiveUsers: number;
    monthlyActiveUsers: number;
    averageSessionDuration: number;
    topCompanions: Array<{
      name: string;
      conversationCount: number;
    }>;
  };
  database: {
    totalUsers: number;
    totalCompanions: number;
    totalConversations: number;
    totalMessages: number;
    databaseSize: string;
  };
}

const AdminMetrics: React.FC = () => {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/admin/metrics', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMetrics(response.data);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Admin access required');
        navigate('/login');
      } else {
        setError('Failed to load metrics');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading metrics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">System Metrics</h1>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Token Usage */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Token Usage</h2>
              <CpuChipIcon className="w-6 h-6 text-gray-400" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Today:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(metrics?.tokenUsage?.today || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">This Week:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(metrics?.tokenUsage?.thisWeek || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">This Month:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(metrics?.tokenUsage?.thisMonth || 0)}
                </span>
              </div>
              <div className="pt-2 border-t">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Cost Today:</span>
                  <span className="text-sm font-medium text-green-600">
                    {formatCurrency(metrics?.tokenUsage?.costToday || 0)}
                  </span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-sm text-gray-600">Cost This Month:</span>
                  <span className="text-sm font-medium text-green-600">
                    {formatCurrency(metrics?.tokenUsage?.costThisMonth || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* API Performance */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">API Performance</h2>
              <ClockIcon className="w-6 h-6 text-gray-400" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Avg Response Time:</span>
                <span className="text-sm font-medium text-gray-900">
                  {metrics?.apiPerformance?.averageResponseTime || 0}ms
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Requests:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(metrics?.apiPerformance?.totalRequests || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Failed Requests:</span>
                <span className="text-sm font-medium text-red-600">
                  {formatNumber(metrics?.apiPerformance?.failedRequests || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Success Rate:</span>
                <span
                  className={`text-sm font-medium ${
                    (metrics?.apiPerformance?.successRate || 0) >= 95
                      ? 'text-green-600'
                      : 'text-yellow-600'
                  }`}
                >
                  {(metrics?.apiPerformance?.successRate || 0).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* User Activity */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">User Activity</h2>
              <ChartBarIcon className="w-6 h-6 text-gray-400" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Daily Active:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(metrics?.userActivity?.dailyActiveUsers || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Weekly Active:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(metrics?.userActivity?.weeklyActiveUsers || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Monthly Active:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(metrics?.userActivity?.monthlyActiveUsers || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Avg Session:</span>
                <span className="text-sm font-medium text-gray-900">
                  {Math.round(metrics?.userActivity?.averageSessionDuration || 0)} min
                </span>
              </div>
            </div>
          </div>

          {/* Database Stats */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Database</h2>
              <CircleStackIcon className="w-6 h-6 text-gray-400" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Users:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(metrics?.database?.totalUsers || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Companions:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(metrics?.database?.totalCompanions || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Conversations:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(metrics?.database?.totalConversations || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Messages:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(metrics?.database?.totalMessages || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Database Size:</span>
                <span className="text-sm font-medium text-gray-900">
                  {metrics?.database?.databaseSize || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Top Companions */}
          <div className="bg-white shadow rounded-lg p-6 md:col-span-2">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Top Companions</h2>
            <div className="space-y-2">
              {metrics?.userActivity?.topCompanions?.map((companion, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{companion.name}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full"
                        style={{
                          width: `${Math.min(
                            100,
                            (companion.conversationCount /
                              Math.max(
                                ...metrics.userActivity.topCompanions.map(
                                  (c) => c.conversationCount,
                                ),
                              )) *
                              100,
                          )}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-12 text-right">
                      {companion.conversationCount}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Refresh Button */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={fetchMetrics}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            Refresh Metrics
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminMetrics;
